import React, { useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import html2canvas from "html2canvas";
import SignatureData from "../../Models/Signature";

interface SignatureModalProps {
  setShowSignaturePad: (show: boolean) => void;
  onSaveSignature: (signedQuote: SignatureData) => void;
  testid: string;
}

const SignatureModal: React.FC<SignatureModalProps> = ({ setShowSignaturePad, onSaveSignature, testid }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePreviewRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const date = new Date().toISOString().split("T")[0];
  const [signature, setSignature] = useState("");
  const [typedSignature, setTypedSignature] = useState<React.ReactNode>("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [useDrawing, setUseDrawing] = useState(false);
  // Billing & Invoice Contact Details — all optional
  const [accountsContactName, setAccountsContactName] = useState("");
  const [accountsEmail, setAccountsEmail] = useState("");
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
  // Inline validation/capture error, shown in the modal instead of a blocking alert().
  const [error, setError] = useState("");

  useEffect(() => {
    // Recompute on every change — including back to empty, otherwise the preview
    // keeps the last generated signature after the name is fully backspaced.
    setTypedSignature(name && !useDrawing ? generateSignature(name) : "");
  }, [name, useDrawing]);

  const generateSignature = (text) => {
    return text.split("").map((char, i) => (
      <span key={i} className="cursive-signature">
        {char}
      </span>
    ));
  };

  const handleStartDrawing = (e) => {
    if (!useDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    setIsDrawing(true);
  };

  const handleDraw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    ctx.stroke();
  };

  const handleEndDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    setSignature(canvas.toDataURL());
  };

  const handleClearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    setSignature("");
  };

 

const handleAdoptSignature = async () => {
  if (!name.trim()) {
    setError("Please enter your full name.");
    return;
  }

  let finalSignature = "";

  if (useDrawing) {
    // If the user chose to draw but the canvas is still blank, block submission.
    if (!signature) {
      setError("Please draw your signature before adopting it.");
      return;
    }
    finalSignature = signature;
  } else {
    // If the user typed their signature, capture the preview element as an image.
    const signatureElement = signaturePreviewRef.current;

    if (signatureElement) {
      const canvas = await html2canvas(signatureElement);
      finalSignature = canvas.toDataURL("image/png"); // Convert to base64
    } else {
      setError("Error capturing typed signature. Please try again.");
      return;
    }
  }

  setError("");

  const signedQuote: SignatureData = {
    testid: testid,
    issigned: true,
    name,
    date,
    position,
    signature: finalSignature, // Always an image
    accountsContactName,
    accountsEmail,
    purchaseOrderNumber,
  };

  
  onSaveSignature(signedQuote);
  setShowSignaturePad(false);
};

  

  return (
    <Modal
      show
      onHide={() => setShowSignaturePad(false)}
      backdrop="static"
      keyboard={false}
      centered
      scrollable
      restoreFocus
    >
      <Modal.Header closeButton>
        <Modal.Title>Adopt Your Signature</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label className="input-label">Full Name</label>
        <input
          type="text"
          placeholder="Enter your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input-field"
        />

        <label className="input-label">Position</label>
        <input
          type="text"
          placeholder="Enter your position"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          className="input-field"
        />

        <h3 className="h5 mt-3">Billing &amp; Invoice Contact Details</h3>

        <label className="input-label">Accounts Contact Name</label>
        <input
          type="text"
          placeholder="Accounts contact name (optional)"
          value={accountsContactName}
          onChange={(e) => setAccountsContactName(e.target.value)}
          className="input-field"
        />

        <label className="input-label">Accounts Email Address</label>
        <input
          type="email"
          placeholder="Accounts email address (optional)"
          value={accountsEmail}
          onChange={(e) => setAccountsEmail(e.target.value)}
          className="input-field"
        />

        <label className="input-label">Purchase Order Number (if applicable)</label>
        <input
          type="text"
          placeholder="Purchase order number (optional)"
          value={purchaseOrderNumber}
          onChange={(e) => setPurchaseOrderNumber(e.target.value)}
          className="input-field"
        />

        <div className="checkbox-container">
          <input
            type="checkbox"
            id="drawSignature"
            checked={useDrawing}
            onChange={() => {
              setUseDrawing(!useDrawing);
              handleClearSignature();
            }}
          />
          <label htmlFor="drawSignature">Or Draw Your Signature</label>
        </div>

        {!useDrawing && (
          <div className="signature-preview" ref={signaturePreviewRef}>
            {typedSignature || "Type your name above"}
          </div>
        )}

        {useDrawing && (
          <canvas
            ref={canvasRef}
            width={350}
            height={100}
            className="signature-canvas"
            onMouseDown={handleStartDrawing}
            onMouseMove={handleDraw}
            onMouseUp={handleEndDrawing}
            onMouseLeave={handleEndDrawing}
          />
        )}

        {error && (
          <div className="signature-error" role="alert" style={{ color: "#dc3545", marginTop: "0.75rem" }}>
            {error}
          </div>
        )}

        <div className="button-container">
          <button className="adopt-button" onClick={handleAdoptSignature}>
            Adopt & Sign
          </button>
          <button className="clear-button" onClick={handleClearSignature}>
            Clear
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SignatureModal;
